/**
 * The configuration domain, as the client will hold it when the configuration track
 * lands its endpoints.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WORK_ORDER_M3_CONFIGURATION_AND_SURFACE.md sections 3.1 and 3.3 name the shapes; this
 * file states them locally and types the layout that waits for them. NOTHING HERE CALLS
 * THE API: the configuration endpoints (decision rules, terminology, calendar
 * enforcement override, the configuration audit) arrive with Track E, and until then
 * the screen renders its layout over absence. When the endpoints land, these interfaces
 * are replaced in kind by the generated schema types and the screen's fetches arrive.
 *
 * The doctrine is already fixed, so the layout is built to it:
 *   - Decision rules are versioned tenant data with one PUBLISHED version per key.
 *     Publishing never rewrites an open record; records carry the pin.
 *   - Terminology overrides change rendered labels per locale; the vocabulary enum
 *     tokens stay canonical.
 *   - The calendar enforcement override is per period, BLOCK or WARN, and the COALESCE
 *     with the kind default happens on the server.
 *   - Every mutation lands in the hash-chained audit log, rendered read only.
 */

/** The rule families at demo depth, from ADR-017. */
export type DecisionRuleFamily = "ALL_LINES_PASS" | "MAX_NEGATIVE_COUNT" | "MANUAL_DECISION";

/** The rule's lifecycle. One PUBLISHED version per object key, per tenant. */
export type DecisionRuleStatus = "DRAFT" | "PUBLISHED" | "RETIRED";

export interface DecisionRule {
  readonly id: string;
  readonly objectKey: string;
  readonly version: number;
  readonly family: DecisionRuleFamily;
  readonly status: DecisionRuleStatus;
  /** The family's own definition: negative_values, and max_negative where it applies. */
  readonly definition: DecisionRuleDefinition;
  readonly effectiveFrom: string | null;
}

export type DecisionRuleDefinition =
  | { readonly family: "ALL_LINES_PASS"; readonly negativeValues: readonly string[] }
  | {
      readonly family: "MAX_NEGATIVE_COUNT";
      readonly negativeValues: readonly string[];
      readonly maxNegative: number;
    }
  | { readonly family: "MANUAL_DECISION" };

export interface TerminologyOverride {
  readonly objectKey: string;
  readonly locale: string;
  readonly value: string;
}

/** The per-period enforcement override (Ruling 16 #2). Null defers to the kind's default. */
export type EnforcementOverride = "BLOCK" | "WARN" | null;

export interface CalendarPeriodEnforcement {
  readonly periodId: string;
  readonly periodName: string;
  readonly kindLabel: string;
  readonly override: EnforcementOverride;
}

/** One hash-chained configuration mutation, rendered read only. */
export interface ConfigurationAuditEntry {
  readonly seq: number;
  readonly eventType: string;
  readonly objectType: string;
  readonly objectId: string;
  readonly actorName: string | null;
  readonly createdAt: string;
}

/**
 * The configuration screen's panels, in the order the work order lists them; wave INST
 * adds the verification types (their RFI notice lead time) after the terminology.
 */
export const CONFIGURATION_PANELS = [
  "terminology",
  "verificationTypes",
  "taskClocks",
  "webhooks",
  "rules",
  "calendar",
  "audit",
] as const;

export type ConfigurationPanel = (typeof CONFIGURATION_PANELS)[number];
